/**
 * The "find a verse" panel. Describe what you need; the assistant suggests a passage to
 * look up. It is a SEARCH HELPER — it never changes the verse on the home screen, and it
 * says so, so nobody expects the hub to update.
 *
 * Like the ask panel, a failed search keeps the words on screen.
 */
import { useId, useRef, useState, type FormEvent } from 'react';
import { searchVerse, explain, type VerseSuggestion } from './assistantApi';
import { Button, ExampleChip, Note, Thinking, cx, textInput } from './ui';

/** Matches MAX_QUERY in worker/routes/assistant.ts. The Worker is what enforces it. */
const MAX_QUERY = 300;

const EXAMPLES = ['something about patience', 'comfort when I’m worried', 'being brave', 'giving thanks'];

export default function VersePanel() {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<VerseSuggestion | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const boxRef = useRef<HTMLInputElement>(null);
  const fieldId = useId();

  const trimmed = query.trim();
  const tooLong = trimmed.length > MAX_QUERY;
  const canSearch = trimmed.length > 0 && !tooLong && !busy;

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (!canSearch) {
      if (!trimmed) setError('Describe what you’re looking for.');
      return;
    }

    setBusy(true);
    setError(null);
    setResult(null);

    try {
      setResult(await searchVerse(trimmed));
    } catch (err) {
      // The description is never cleared, so it is still on screen.
      setError(explain(err, 'Couldn’t find a passage just now.'));
      boxRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label htmlFor={fieldId} className="block text-sm font-medium text-ink">
          Find a verse
        </label>
        <p id={`${fieldId}-hint`} className="mt-1 text-sm leading-relaxed text-ink-soft">
          Describe what you need and get a passage to look up. This won’t change the verse on the
          home screen.
        </p>
        <input
          id={fieldId}
          ref={boxRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-describedby={`${fieldId}-hint`}
          aria-invalid={tooLong || undefined}
          placeholder="e.g. something about patience"
          className={cx('mt-3', textInput, tooLong && 'border-over')}
        />
      </div>

      {!result && !busy && (
        <div className="flex flex-wrap gap-2">
          {EXAMPLES.map((ex) => (
            <ExampleChip
              key={ex}
              onClick={() => {
                setQuery(ex);
                setError(null);
                boxRef.current?.focus();
              }}
            >
              {ex}
            </ExampleChip>
          ))}
        </div>
      )}

      {tooLong && (
        <Note tone="warn" role="alert">
          Keep the description under {MAX_QUERY} characters.
        </Note>
      )}
      {error && (
        <Note tone="warn" role="alert">
          {error} Your words are still here — try again.
        </Note>
      )}

      <div className="flex justify-end">
        <Button type="submit" variant="primary" disabled={!canSearch}>
          {busy ? 'Looking…' : 'Find a verse'}
        </Button>
      </div>

      {busy && (
        <div className="pt-1">
          <Thinking label="Looking…" />
        </div>
      )}

      {result && !busy && (
        <div className="rounded-2xl border border-sage/30 bg-sage-soft p-4">
          <h3 className="sr-only">Suggested passage</h3>
          <p className="font-display text-xl font-semibold text-sage">{result.reference}</p>
          {result.why && <p className="mt-2 text-sm leading-relaxed text-ink">{result.why}</p>}
          <p className="mt-3 text-xs text-ink-faint">
            A suggestion to look up — open your Bible or read it there.
          </p>
        </div>
      )}
    </form>
  );
}
